import { Suspense, type ReactNode } from "react";
import { DeadlineExceededError } from "@-/dev.improve.haniverse.lib.deadline";
import { useReadinessDeadline } from "@-/dev.improve.haniverse.lib.readiness-handshake";
import { SUSPENSE_DEADLINE_MS } from "#suspense-deadline-ms";

/**
 * Renders the fallback, and throws a `DeadlineExceededError` in its place once the boundary has
 * been suspended for `deadlineMs`. A throw from a fallback propagates to the nearest error boundary
 * ABOVE the Suspense boundary, which is exactly where the page's error screen (retry, return home,
 * restart) lives. Its message says "timed out", so every surface classifies it as retryable.
 */
function SuspenseDeadlineFallback({
  children,
  deadlineMs,
  operation,
}: {
  children: ReactNode;
  deadlineMs: number;
  operation: string;
}) {
  // A fallback is mounted exactly while its boundary is suspended, so "waiting" is simply "mounted";
  // a boundary that resolves unmounts this and disarms the deadline.
  const expired = useReadinessDeadline({ deadlineMs, operation, waiting: true });
  if (expired) {
    throw new DeadlineExceededError(operation, deadlineMs);
  }
  return children;
}

/**
 * `Suspense` that cannot stay suspended forever — the only Suspense boundary app code may use.
 *
 * Requires an error boundary above it (every page has one: `SuspensePageBoundary`, and the root
 * boundary behind that), because expiry is delivered as a thrown error rather than a new state:
 * the error screen already owns retry and the ways out, so a stuck load reuses it instead of
 * inventing a second, weaker one.
 */
export function BoundedSuspense({
  children,
  deadlineMs = SUSPENSE_DEADLINE_MS,
  fallback,
  operation,
}: {
  children: ReactNode;
  deadlineMs?: number;
  fallback: ReactNode;
  /** Names what the boundary is loading, for the error and the hang report. */
  operation: string;
}) {
  return (
    <Suspense
      fallback={
        <SuspenseDeadlineFallback deadlineMs={deadlineMs} operation={operation}>
          {fallback}
        </SuspenseDeadlineFallback>
      }
    >
      {children}
    </Suspense>
  );
}
